"use client"

import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { BackupView } from "@/app/deployment/backup/page";
import { Input } from "../ui/input";
import { checkInputFlipBoolean } from "@/helpers/input.helper";
import { BackupStatus } from "@/constants/status";
import { SuccessMessage } from "../ui/successMessage";
import { ErrorMessage } from "../ui/errorMessage";
import { backuplocalcleanup, backupsql, backupupdateprod, backupzippingsequence, transferbackup } from "@/services/backup.service";
import { ProgressMessage } from "../ui/progressMessage";
import Loading from "@/components/ui/loading";
import { BackupProgress } from "@/constants/backup";

interface Props {
    domains: BackupView[],
    onBackupSuccess: () => void,
    onBackupFailed: () => void
}

interface DomainBackup {
    domain: BackupView,
    status: BackupStatus
}

export default function BackupAllModal(props: Props) {
    const [open, setOpen] = useState(false);
    const [upgradeStatuses, setUpgradeStatuses] = useState<DomainBackup[]>([]);
    const [backupInProgress, setBackupInProgress] = useState<BackupProgress>(BackupProgress.standBy);
    const [backupsDone, setBackupsDone] = useState<number>(0);
    const [backupsFailed, setBackupsFailed] = useState<number>(0);
    const [failedBackups, setFailedBackups] = useState<string[]>([]);
    const [isDisabled, setIsDisabled] = useState(true);

    const steps = useMemo(() => [
        {
            title: "Zipping Sequence",
            expectedReturn: "Zipping Done",
            functionToRun: backupzippingsequence,
            data: []
        },
        {
            title: "Backup SQL",
            expectedReturn: "Backup SQL Done",
            functionToRun: backupsql,
            data: []
        },
        {
            title: "Local Cleanup",
            expectedReturn: "Local Cleanup Done",
            functionToRun: backuplocalcleanup,
            data: []
        },
        {
            title: "Transfer Backup",
            expectedReturn: "Transfer Backup Done",
            functionToRun: transferbackup,
            data: []
        },
        {
            title: "Backup Done",
            expectedReturn: "Backup Done",
            functionToRun: backupupdateprod,
            data: []
        },
    ], []);

    useEffect(() => {
        const setupDomains = () => {
            const domains = props.domains.map(domain => {
                return {domain: domain, status: BackupStatus.standBy}
            });
            setUpgradeStatuses(domains);
        }

        if(upgradeStatuses.length === 0){
            setupDomains();
        }
    }, [props.domains, upgradeStatuses.length]);
    
    const formatFormData = useCallback(
        (domainID:string, zipName:string) => {
          const formData = new FormData();
          formData.append("domainId", domainID);
          formData.append("zipName", zipName);
          return formData;
        },
        []
    );

    const startStep = useCallback(async (domainID: string, currentStep: number, zipName: string) => {
        let result = null;
        if(steps[currentStep].functionToRun != undefined){
            result = await steps[currentStep].functionToRun(
                formatFormData(domainID, zipName)
            );
            if (result.result == steps[currentStep].expectedReturn) {
                if(zipName.length <= 0 && (result.data.zipName != undefined && result.data.zipName.length > 0)){
                    zipName = result.data.zipName;
                }

                currentStep++;
                if(currentStep < steps.length){
                    if(zipName.length > 0){
                        await startStep(domainID, currentStep, zipName ?? result.data.zipName);
                    } else {
                        await startStep(domainID, currentStep, "");
                    }
                }

                return true;
            }
        }
      }, [
        steps,
        formatFormData,
      ]);

    async function nextBackup(position: number){
        console.log("Begnning with: " + props.domains[position].Name);
        let result = null;
        try {
            result = await startStep(props.domains[position].ID, 0, "");
        } catch (error) {
            console.log(error);
            const currentFailedDomains = failedBackups;
            currentFailedDomains.push(props.domains[position].Name);
            setFailedBackups(currentFailedDomains);
        }

        console.log("Done with: " + props.domains[position].Name);
        if(result != null){
            setBackupsDone(position + 1);
        } else {
            let failedCount = backupsFailed;
            setBackupsFailed(failedCount++);
        }

        if(position == props.domains.length - 1){
            if(failedBackups.length > 0){
                console.log('Failed Backups: ', failedBackups);
            }
            setBackupInProgress(BackupProgress.done);
            props.onBackupSuccess();
            setOpen(false);
            return;
        } else {
            position++;
            await nextBackup(position);
        }
    }

    async function initiateBackupAll() {
        setBackupInProgress(BackupProgress.inProgress);

        await nextBackup(0);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Backup All Domains</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Backup All Domains</DialogTitle>
                <DialogDescription>
                    You sure you want to backup all {props.domains.length} domains?
                </DialogDescription>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            <Input title="CONFIRM" placeholder="Enter CONFIRM" onChange={(e) => checkInputFlipBoolean(e.target.value, setIsDisabled)}/>
                            <Button className="cursor-pointer mb-3 mt-3" id="initiateBackupAll" disabled={isDisabled} hidden onClick={(_e) => initiateBackupAll()}>Backup All</Button>

                            {backupInProgress === BackupProgress.inProgress && (
                                <>
                                    <ProgressMessage>
                                        <Loading text={"Backing up..."} />
                                    </ProgressMessage>
                                    <div className="bg-green-500 rounded-lg py-2 px-3 mb-3 text-center">
                                        Done so far: {backupsDone} / {props.domains.length}
                                    </div>
                                    {backupsFailed > 0 && (
                                        <div className="bg-red-500 rounded-lg py-2 px-3 mb-3 text-center">
                                            Backups Failed: {backupsFailed}
                                        </div>
                                    )}
                                </>
                            )}

                            {backupInProgress === BackupProgress.failed && (
                                <ErrorMessage>
                                    <p>Backups failed.</p>
                                </ErrorMessage>
                            )}

                            {backupInProgress === BackupProgress.done && (
                                <SuccessMessage>
                                    <p>Backups completed!</p>
                                    <p>Page will refresh momentarily</p>
                                </SuccessMessage>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}